import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { AlertTriangle, CreditCard, RefreshCw, FileText, Clock, Calendar } from 'lucide-react'
import { Card, Button } from '../ui'
import { useAuthStore } from '../../stores/authStore'
import { organizationService, Organization } from '../../services/organizationService'
import { invoiceService } from '../../services/invoiceService'
import { subscriptionService, Subscription } from '../../services/subscriptionService'
import { useToast } from '../../contexts/ToastContext'
import { getSubscriptionTierDisplayName } from '../../utils/subscriptionHelpers'
import UpgradeSubscriptionModal from './UpgradeSubscriptionModal'
import CancelSubscriptionModal from './CancelSubscriptionModal'
import PaymentMethodsSection from './PaymentMethodsSection'
import AddPaymentMethodModal from './AddPaymentMethodModal'
import InvoicesSection from './InvoicesSection'

// Helper function to calculate days remaining
const calculateDaysRemaining = (endDate: string | null): number => {
  if (!endDate) return 0
  const end = new Date(endDate)
  const now = new Date()
  const diffTime = end.getTime() - now.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return Math.max(0, diffDays)
}

// Format date for display
const formatDate = (dateString: string | null): string => {
  if (!dateString) return ''
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  } catch {
    return ''
  }
}

// Helper to get display name for subscription status
const getStatusDisplayName = (status: string): string => {
  if (status === 'trialing' || status === 'trial') {
    return 'Free Trial'
  }
  return status.replace(/_/g, ' ')
}

const BillingSettings: React.FC = () => {
  const { user } = useAuthStore()
  const { showError, showSuccess } = useToast()
  const [loading, setLoading] = useState(true)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] = useState(false)
  const [retryingPayment, setRetryingPayment] = useState(false)
  const [paymentMethodsKey, setPaymentMethodsKey] = useState(0)

  // Calculate trial and grace period info
  const trialDaysRemaining = subscription?.trial_end ? calculateDaysRemaining(subscription.trial_end) : 0
  const isTrialing = subscription?.status === 'trialing' && trialDaysRemaining > 0
  const gracePeriodEnd = subscription?.current_period_end
  const graceDaysRemaining = organization?.subscriptionStatus === 'past_due' && gracePeriodEnd
    ? calculateDaysRemaining(gracePeriodEnd)
    : 0

  // Fetch billing data on mount
  useEffect(() => {
    const fetchBillingData = async () => {
      if (!user?.organizationId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)

        // Fetch organization details
        const orgResponse = await organizationService.getOrganization(
          user.organizationId
        )

        if (orgResponse.success && orgResponse.data) {
          setOrganization(orgResponse.data)

          // Fetch subscription details for trial/grace period info
          const subResponse = await subscriptionService.getCurrentSubscription()
          if (subResponse.success && subResponse.data?.subscription) {
            setSubscription(subResponse.data.subscription)
          }
        } else {
          showError(
            'Failed to load billing information',
            'Could not fetch organization details'
          )
        }
      } catch (error) {
        console.error('Error fetching billing data:', error)
        showError(
          'Load Failed',
          error instanceof Error
            ? error.message
            : 'Failed to load billing data'
        )
      } finally {
        setLoading(false)
      }
    }

    fetchBillingData()
  }, [user?.organizationId, showError])

  // Handle retry payment for failed invoices
  const handleRetryPayment = async () => {
    try {
      setRetryingPayment(true)

      // First, get the most recent open/failed invoice
      const failedInvoice = await invoiceService.getLatestFailedInvoice()

      if (!failedInvoice) {
        showError('No Failed Invoice', 'No pending invoice found to retry.')
        return
      }

      // Retry the payment
      const response = await invoiceService.retryPayment(failedInvoice.stripe_invoice_id)

      if (response.success) {
        showSuccess('Payment Successful', 'Your payment has been processed successfully.')
        // Refresh organization data to update subscription status
        if (user?.organizationId) {
          const orgResponse = await organizationService.getOrganization(user.organizationId)
          if (orgResponse.success && orgResponse.data) {
            setOrganization(orgResponse.data)
          }
        }
      } else {
        showError('Payment Failed', response.message || 'Please update your payment method and try again.')
      }
    } catch (error) {
      console.error('Error retrying payment:', error)
      showError('Error', 'Failed to process payment. Please try again.')
    } finally {
      setRetryingPayment(false)
    }
  }

  // Show loading state while fetching data
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className='space-y-6'
      >
        <Card variant='outlined' padding='lg'>
          <div className='flex items-center justify-center py-8'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-avi-primary'></div>
          </div>
        </Card>
      </motion.div>
    )
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        role='tabpanel'
        id='billing-panel'
        aria-labelledby='billing-tab'
        className='space-y-6'
      >
        {/* Trial Banner - Show when subscription is trialing */}
        {isTrialing && subscription && (
          <div className='bg-blue-50 border border-blue-200 rounded-xl p-4'>
            <div className='flex items-start gap-3'>
              <div className='p-2 bg-blue-100 rounded-lg'>
                <Clock className='h-5 w-5 text-blue-600' />
              </div>
              <div className='flex-1'>
                <div className='flex items-center gap-2'>
                  <h3 className='text-sm font-semibold text-blue-800'>
                    Free Trial
                  </h3>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                    trialDaysRemaining <= 3
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining
                  </span>
                </div>
                <p className='text-sm text-blue-700 mt-1'>
                  Your trial ends on {formatDate(subscription.trial_end)}.
                  {trialDaysRemaining <= 7 && ' Add a payment method to continue using all features after your trial.'}
                </p>
                {trialDaysRemaining <= 7 && (
                  <div className='mt-3'>
                    <Button
                      variant='outline'
                      size='xs'
                      onClick={() => setShowAddPaymentMethodModal(true)}
                      className='border-blue-300 text-blue-700 hover:bg-blue-100'
                    >
                      <CreditCard className='h-4 w-4 mr-1.5' />
                      Add Payment Method
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Cancellation Pending Banner - Show when subscription will cancel at period end */}
        {subscription?.cancel_at_period_end && subscription.status !== 'canceled' && (
          <div className='bg-gray-50 border border-gray-200 rounded-xl p-4'>
            <div className='flex items-start gap-3'>
              <div className='p-2 bg-gray-100 rounded-lg'>
                <Calendar className='h-5 w-5 text-gray-600' />
              </div>
              <div className='flex-1'>
                <h3 className='text-sm font-semibold text-gray-800'>
                  Cancellation Scheduled
                </h3>
                <p className='text-sm text-gray-600 mt-1'>
                  Your subscription will end on {formatDate(subscription.current_period_end)}.
                  You'll retain access to all features until then.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Grace Period Banner - Show when subscription is past_due */}
        {organization?.subscriptionStatus === 'past_due' && (
          <div className={`rounded-xl p-4 border ${
            graceDaysRemaining <= 3
              ? 'bg-red-50 border-red-300'
              : graceDaysRemaining <= 7
              ? 'bg-orange-50 border-orange-200'
              : 'bg-yellow-50 border-yellow-200'
          }`}>
            <div className='flex items-start gap-3'>
              <div className={`p-2 rounded-lg ${
                graceDaysRemaining <= 3
                  ? 'bg-red-100'
                  : graceDaysRemaining <= 7
                  ? 'bg-orange-100'
                  : 'bg-yellow-100'
              }`}>
                <AlertTriangle className={`h-5 w-5 ${
                  graceDaysRemaining <= 3
                    ? 'text-red-600'
                    : graceDaysRemaining <= 7
                    ? 'text-orange-600'
                    : 'text-yellow-600'
                }`} />
              </div>
              <div className='flex-1'>
                <div className='flex items-center gap-2'>
                  <h3 className={`text-sm font-semibold ${
                    graceDaysRemaining <= 3
                      ? 'text-red-800'
                      : graceDaysRemaining <= 7
                      ? 'text-orange-800'
                      : 'text-yellow-800'
                  }`}>
                    Payment Failed
                  </h3>
                  {graceDaysRemaining > 0 && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                      graceDaysRemaining <= 3
                        ? 'bg-red-100 text-red-700'
                        : graceDaysRemaining <= 7
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {graceDaysRemaining} {graceDaysRemaining === 1 ? 'day' : 'days'} to update
                    </span>
                  )}
                </div>
                <p className={`text-sm mt-1 ${
                  graceDaysRemaining <= 3
                    ? 'text-red-700'
                    : graceDaysRemaining <= 7
                    ? 'text-orange-700'
                    : 'text-yellow-700'
                }`}>
                  {graceDaysRemaining <= 3
                    ? 'Urgent: Your account will be suspended soon. Update your payment method immediately.'
                    : graceDaysRemaining <= 7
                    ? 'Your payment has failed. Please update your payment method to avoid service interruption.'
                    : 'Your payment has failed. You have time to resolve this before your service is affected.'}
                </p>
                <div className='flex flex-wrap gap-2 mt-3'>
                  <Button
                    variant='outline'
                    size='xs'
                    onClick={() => setShowAddPaymentMethodModal(true)}
                    className={`${
                      graceDaysRemaining <= 3
                        ? 'border-red-300 text-red-700 hover:bg-red-100'
                        : graceDaysRemaining <= 7
                        ? 'border-orange-300 text-orange-700 hover:bg-orange-100'
                        : 'border-yellow-300 text-yellow-700 hover:bg-yellow-100'
                    }`}
                  >
                    <CreditCard className='h-4 w-4 mr-1.5' />
                    Update Payment Method
                  </Button>
                  <Button
                    variant='outline'
                    size='xs'
                    onClick={handleRetryPayment}
                    disabled={retryingPayment}
                    className={`${
                      graceDaysRemaining <= 3
                        ? 'border-red-300 text-red-700 hover:bg-red-100'
                        : graceDaysRemaining <= 7
                        ? 'border-orange-300 text-orange-700 hover:bg-orange-100'
                        : 'border-yellow-300 text-yellow-700 hover:bg-yellow-100'
                    }`}
                  >
                    <RefreshCw className={`h-4 w-4 mr-1.5 ${retryingPayment ? 'animate-spin' : ''}`} />
                    {retryingPayment ? 'Retrying...' : 'Retry Payment'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Overview Card */}
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
          <div className='px-5 sm:px-6 py-4'>
            <div className='flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4'>
              <div className='flex items-start gap-3'>
                <div className='p-2 bg-blue-50 rounded-lg border border-blue-100'>
                  <CreditCard className='h-5 w-5 text-blue-600' />
                </div>
                <div className='flex-1'>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1'>
                    Current Plan
                  </p>
                  {organization && organization.subscriptionStatus && (
                    <div className='flex items-center gap-3'>
                      {(() => {
                        const tierName = getSubscriptionTierDisplayName(
                          organization.subscriptionTier
                        )
                        return tierName && tierName !== 'N/A' ? (
                          <h2 className='text-xl lg:text-2xl font-bold text-gray-900 tracking-tight'>
                            {tierName}
                          </h2>
                        ) : (
                          <h2 className='text-xl lg:text-2xl font-bold text-gray-900 tracking-tight'>
                            No Plan
                          </h2>
                        )
                      })()}
                      <div
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border ${
                          organization.subscriptionStatus === 'active'
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                            : organization.subscriptionStatus === 'trialing' || organization.subscriptionStatus === 'trial'
                            ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
                            : 'bg-red-50 border-red-200 text-red-700'
                        }`}
                      >
                        <div
                          className={`h-1.5 w-1.5 rounded-full ${
                            organization.subscriptionStatus === 'active'
                              ? 'bg-emerald-500 animate-pulse'
                              : organization.subscriptionStatus === 'trialing' || organization.subscriptionStatus === 'trial'
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                        />
                        <span className='text-xs font-semibold uppercase'>
                          {getStatusDisplayName(organization.subscriptionStatus)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Subscription Action Buttons */}
              <div className='flex items-center gap-2'>
                {/* Show Cancel button only for active/trialing subscriptions that aren't already pending cancellation */}
                {subscription &&
                 (subscription.status === 'active' || subscription.status === 'trialing') &&
                 !subscription.cancel_at_period_end && (
                  <Button
                    variant='outline'
                    size='xs'
                    onClick={() => setShowCancelModal(true)}
                    className='text-gray-600 border-gray-300 hover:bg-gray-50'
                  >
                    Cancel Plan
                  </Button>
                )}
                <Button
                  variant='primary'
                  size='xs'
                  onClick={() => setShowUpgradeModal(true)}
                >
                  Upgrade Subscription
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Billing Details Card */}
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
          <div className='px-6 sm:px-8 py-6'>
            {/* Payment Methods Section */}
            <div className='mb-6'>
              <h3 className='text-sm font-semibold text-gray-900 mb-1'>
                Payment Methods
              </h3>
              <p className='text-sm text-gray-600'>
                Manage your organization's payment methods for subscriptions
              </p>
            </div>

            <PaymentMethodsSection
              key={paymentMethodsKey}
              onAddPaymentMethod={() => setShowAddPaymentMethodModal(true)}
            />

            {/* Invoices Section */}
            <div className='mt-8 mb-6'>
              <h3 className='text-sm font-semibold text-gray-900 mb-1'>
                <div className='flex items-center gap-2'>
                  <FileText className='h-4 w-4 text-gray-500' />
                  Invoices
                </div>
              </h3>
              <p className='text-sm text-gray-600'>
                View your billing invoices
              </p>
            </div>

            <InvoicesSection />
          </div>
        </div>
      </motion.div>

      {/* Upgrade Subscription Modal */}
      <UpgradeSubscriptionModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgradeSuccess={() => {
          // Refresh organization data after upgrade
          if (user?.organizationId) {
            organizationService.getOrganization(user.organizationId).then((response) => {
              if (response.success && response.data) {
                setOrganization(response.data)
              }
            })
          }
        }}
      />

      {/* Add Payment Method Modal */}
      <AddPaymentMethodModal
        isOpen={showAddPaymentMethodModal}
        onClose={() => setShowAddPaymentMethodModal(false)}
        onSuccess={() => {
          setShowAddPaymentMethodModal(false)
          // Trigger refresh of payment methods list
          setPaymentMethodsKey(prev => prev + 1)
        }}
      />

      {/* Cancel Subscription Modal */}
      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onCancelSuccess={() => {
          // Refresh organization and subscription data after cancellation
          if (user?.organizationId) {
            organizationService.getOrganization(user.organizationId).then((response) => {
              if (response.success && response.data) {
                setOrganization(response.data)
              }
            })
            subscriptionService.getCurrentSubscription().then((response) => {
              if (response.success && response.data?.subscription) {
                setSubscription(response.data.subscription)
              }
            })
          }
        }}
        subscription={subscription}
      />
    </>
  )
}

export default BillingSettings
